import { useCallback, useMemo, useRef, useState } from 'react';
import { Column } from 'react-table';
import { Card, Col, Container, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import ServerPaginatedTable from 'src/components/ServerPaginatedTable/ServerPaginatedTable';
import TableSkeleton from 'src/components/TableSkeleton/TableSkeleton';
import type { CatalogRow } from 'src/motornet/types';
import { useCatalogRows } from './api';
import AssociationModal from './AssociationModal';
import CatalogFilters from './CatalogFilters';
import { createColumnsData, ExpandedChip } from './columnsData';
import { CatalogFilterState, Chip, EMPTY_FILTERS } from './helpers';
import MotornetPanel from './MotornetPanel';
import ProviderPanel from './ProviderPanel';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

const VehicleAssociationPage = () => {
	const { t } = useTranslation();

	const [filters, setFilters] = useState<CatalogFilterState>(EMPTY_FILTERS);
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [expanded, setExpanded] = useState<ExpandedChip>(null);
	const [useModal, setUseModal] = useState(false);

	const goToPage = (next: number) => {
		setPage(next);
		setExpanded(null);
	};

	const { data, isPending, isFetching, error } = useCatalogRows({
		...filters,
		search: search || undefined,
		page,
		pageSize,
	});

	const items = useMemo(() => data?.items ?? [], [data]);
	const filterKey = JSON.stringify([filters, search]);
	const lastKnownTotal = useRef(0);
	const dataFilter = useRef(filterKey);
	if (data) {
		lastKnownTotal.current = data.total;
		dataFilter.current = filterKey;
	}
	const totalCount = data?.total ?? lastKnownTotal.current;
	const isFilterChanging = filterKey !== dataFilter.current;

	const toggleChip = useCallback(
		(versionKey: string, chip: Chip) =>
			setExpanded((current) =>
				current?.versionKey === versionKey && current.chip === chip ? null : { versionKey, chip }
			),
		[]
	);
	const closePanel = useCallback(() => setExpanded(null), []);
	const expandedRow = expanded ? items.find((row) => row.versionKey === expanded.versionKey) : undefined;

	const columns = useMemo(
		() => createColumnsData(expanded, toggleChip) as Column<object>[],
		[expanded, toggleChip]
	);

	const controlState = useCallback(
		(state: any) => ({ ...state, expanded: expanded ? { [expanded.versionKey]: true } : {} }),
		[expanded]
	);

	const renderRowSubComponent = useCallback(
		({ row }: { row: { original: CatalogRow } }) => {
			if (!expanded || expanded.versionKey !== row.original.versionKey) return null;
			return expanded.chip === 'motornet' ? (
				<MotornetPanel key="motornet" row={row.original} onClose={closePanel} />
			) : (
				<ProviderPanel key={expanded.chip} row={row.original} provider={expanded.chip} onClose={closePanel} />
			);
		},
		[expanded, closePanel]
	);

	return (
		<Container fluid>
			<Row className="align-items-center">
				<Col md={12} className="display-6">
					{t('vehicle_associations', { defaultValue: 'Associazione veicoli' })}
				</Col>
				{/* <Col md={6} className="d-flex justify-content-md-end">
					<Form.Check
						type="switch"
						id="vehicle-association-modal"
						label={t('vehicle_association_use_modal', { defaultValue: 'Finestra di dialogo' })}
						checked={useModal}
						onChange={(event) => {
							setUseModal(event.target.checked);
							setExpanded(null);
						}}
					/>
				</Col> */}
			</Row>

			<CatalogFilters
				filters={filters}
				search={search}
				onChange={(nextFilters, nextSearch) => {
					setFilters(nextFilters);
					setSearch(nextSearch);
					goToPage(1);
				}}
			/>

			{!error && (
				<div className="table-results-container">
					{t('show', { defaultValue: 'Mostra' })}:
					<span className="table-result-count ms-2">{totalCount}</span>
				</div>
			)}
			{error ? (
				<Card className="my-4">
					<Card.Body className="text-center text-danger py-5">
						{error.message || t('error_loading_data', { defaultValue: 'Errore non specificato durante il caricamento dei dati' })}
					</Card.Body>
				</Card>
			) : isFilterChanging ? (
				<TableSkeleton cols={columns.length} rows={pageSize} />
			) : (
				<div  style={{ opacity: isFetching && !isPending ? 0.6 : 1 }}>
					<ServerPaginatedTable
						key={`${filterKey}-${pageSize}-${useModal}`}
						columns={columns}
						data={items}
						loading={isPending}
						error={error}
						totalCount={totalCount}
						pageSize={pageSize}
						showPageSelector
						pageSizeOptions={PAGE_SIZE_OPTIONS}
						initialPage={page}
						onPageSizeChange={(newSize: number) => {
							setPageSize(newSize);
							goToPage(1);
						}}
						loadFn={(skip: number, limit: number) => goToPage(Math.floor(skip / limit) + 1)}
						isExpandable={!useModal}
						getRowId={(row: CatalogRow) => row.versionKey}
						useControlledState={controlState}
						renderRowSubComponent={renderRowSubComponent}
					/>
				</div>
			)}
			{useModal && <AssociationModal row={expandedRow} chip={expanded?.chip} onClose={closePanel} />}
		</Container>
	);
};

export default VehicleAssociationPage;
